import { useEffect, useState, useContext } from 'react';
import { Box, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Typography } from '@mui/material';
import { useForm } from 'react-hook-form';
import { Controls, Components } from 'lib';
import { useDispatch, useSelector } from 'react-redux';
import axios, { withCredentials } from 'util/axiosInstance';
import { profileActionCreators } from 'redux/actionCreators';
import { INotify } from 'lib/types';
import { IProfileEmailAddressesObject } from '../../../@types/profile-types';
import { emailLookupSelector, personalInfoSelector } from 'redux/selectors';
import { IEmailFormInputs, formDefaults, formSchemaResolver, styles } from './emailForm.helper'
import { emailAddressConfig } from './contact.helper';
import { areObjectDifferent } from 'util/object.util';
import { ThemeContext } from 'util/themes/themeContext';
import LoadingButton from '@mui/lab/LoadingButton';
import { Close as CloseIcon } from '@mui/icons-material';

type EmailPageType = 'PROFILE' | 'ONBOARDING';

interface IEmailForm {
    modalOpen: boolean;
    handleModalClose: Function;
    modalEditData: IProfileEmailAddressesObject | null;
    type: EmailPageType;
    autoSubmit?: boolean;
}

export const getEmailForm = (modalEditData: any, submitFormValues: Function, typeOptions: Partial<HTMLInputElement>[], pageType: EmailPageType, autoSubmit: boolean | undefined, selectedTheme: any) => {
    const [defaultFormData, setDefaultFormData] = useState<IEmailFormInputs>(formDefaults);
    const inputStyleProp = {
        ...styles[pageType].controlPadding,
        ...selectedTheme?.typography?.Components?.inputText?.default,
        ...selectedTheme?.typography?.Components?.inputText?.dark
    };
    const labelStyleProp = {
        ...styles[pageType].labelStyles,
        ...selectedTheme?.typography?.Components?.inputLabel?.default,
        ...selectedTheme?.typography?.Components?.inputLabel?.dark
    };
    const submitForm = async (formValues: any) => {
        submitFormValues(formValues, defaultFormData);
    }

    const {
        handleSubmit,
        reset: formReset,
        control,
        formState: { errors },
        setValue
    } = useForm<IEmailFormInputs>({
        resolver: formSchemaResolver,
        defaultValues: defaultFormData
    });

    useEffect(() => {
        if (autoSubmit) {
            handleSubmit(submitForm)();
        }
        return;
    }, [autoSubmit]);


    useEffect(() => {
        const formData = JSON.parse(JSON.stringify(formDefaults));
        if (modalEditData && modalEditData.asset_id) {
            formData.emailAddress = modalEditData.emailAddress || '';
            formData.type = modalEditData.type || '';
            formData.asset_id = modalEditData.asset_id;
            formData.isPrimary = modalEditData.isPrimary || false;
        }
        formReset(formData);
        setDefaultFormData(formData);
    }, [modalEditData]);

    const emailLabel = pageType === 'ONBOARDING' ? 'Personal Email' : 'E-mail';

    return (
        <Grid container display={'flex'} direction={'row'} component="form" id={'email_form'} onSubmit={handleSubmit(submitForm)}>
            <Grid item xs={12} display={pageType === 'ONBOARDING' ? 'none' : 'block'}>
                <Controls.Checkbox
                    name="primary"
                    label="Primary"
                    value={defaultFormData.isPrimary}
                    checkedColor={selectedTheme?.palette?.primary[500]}
                    unCheckedColor={selectedTheme?.palette?.secondary?.grayScale && selectedTheme?.palette?.secondary?.grayScale[600]}
                    control={control}
                    error={errors?.isPrimary}
                    inputStyleProps={inputStyleProp}
                />
            </Grid>
            <Grid item xs={12} display={pageType === 'ONBOARDING' ? 'none' : 'block'}>
                <Controls.TextSelectWithOtherDropdown
                    name="type"
                    label="Email Type"
                    defaultValue={defaultFormData.type}
                    options={typeOptions}
                    control={control}
                    errors={errors}
                    setValue={setValue}
                    labelStyles={labelStyleProp}
                    inputStyleProps={inputStyleProp}
                    inputInlineStyle={{ ...selectedTheme?.input }}
                    required
                />
            </Grid>
            <Grid item xs={12}>
                <Controls.Input
                    name="emailAddress"
                    label={emailLabel}
                    defaultValue={defaultFormData.emailAddress}
                    control={control}
                    error={errors?.emailAddress || ''}
                    inputStyleProps={inputStyleProp}
                    sx={{ ...selectedTheme?.input?.dark }}
                    labelStyles={labelStyleProp}
                    required
                />
            </Grid>
        </Grid>
    );
}

export const EmailForm = (props: IEmailForm) => {
    const { modalOpen, modalEditData, type, autoSubmit } = props;
    const dispatch = useDispatch();
    const [, setDidMount] = useState(false); 
    const { selectedTheme } = useContext(ThemeContext);

    const personalInfo = useSelector(personalInfoSelector.selectPersonalInfoData);

    const [notify, setNotify] = useState<INotify>({ isOpen: false, message: '', type: 'success' });
    const [isLoading, setLoading] = useState<boolean>(false);

    let emailLookupData = useSelector(emailLookupSelector.selectEmailLookupData);

    if (type === 'ONBOARDING') {
        emailLookupData = emailLookupData.filter((x) => x.value !== '__other__');
    }

    const typeOptions: Partial<HTMLInputElement>[] = [];
    for (const email of emailLookupData) {
        typeOptions.push({ id: email.value, title: email.display });
    }

    const handleModalClose = () => {
        props.handleModalClose();
    };

    useEffect(() => {
        setDidMount(true);
        return () => {
            setDidMount(false);
        }
    }, []);

    const submitForm = async (formValues: any, defaultFormData: IEmailFormInputs) => {
        const requestValues = JSON.parse(JSON.stringify(formValues));
        setLoading(true);
        if (requestValues.type === '__other__') {
            requestValues.type = requestValues.otherType;
        }
        const sendApiRequest = areObjectDifferent(formValues, defaultFormData, ['type', 'emailAddress', 'isPrimary']);
        if (!sendApiRequest) {
            handleModalClose();
            return;
        }
        delete requestValues.otherType;
        try {
            if (!defaultFormData.asset_id) {
                delete requestValues.asset_id;
                await axios().put(emailAddressConfig.endpoint, requestValues, withCredentials());
                setLoading(false);
            } else {
                requestValues.asset_id = defaultFormData.asset_id;
                requestValues.userId = personalInfo.userId;
                delete requestValues.asset_id;

                await axios().put(`${emailAddressConfig.endpoint}/${defaultFormData.asset_id}`, requestValues, withCredentials());
                setLoading(false);
            }
            dispatch(profileActionCreators.getPersonalDynamicCardsInfo(emailAddressConfig));
            setNotify({ message: 'Success.', type: 'success', isOpen: true });
            handleModalClose();
        } catch (error) {
            setLoading(false);
            setNotify({ message: 'An error occurred. Please try later!', type: 'error', isOpen: true });
        }
    };

    if (type === 'PROFILE') {
        return (
            <>
                <Components.Notify notify={notify} setNotify={setNotify} />
                <Dialog
                    maxWidth="sm"
                    fullWidth={true}
                    open={modalOpen}
                    onClose={handleModalClose}
                >
                    <DialogTitle data-testid="email_form_title"
                        style={{display:'flex',justifyContent:'space-between'}}
                    >
                        <Typography component={'span'} sx={{
                            letterSpacing: 0,
                            ...selectedTheme?.typography?.h4,
                            color: selectedTheme?.palette.secondary?.grayScale && selectedTheme?.palette.secondary?.grayScale[800]
                        }}>
                            {modalEditData ? 'Edit' : 'Add'} Email Info
                        </Typography>
                        <CloseIcon className='cursor-pointer' onClick={handleModalClose}/>
                    </DialogTitle>
                    <DialogContent dividers={true}>
                        {
                            getEmailForm(modalEditData, submitForm, typeOptions, type, autoSubmit, selectedTheme)
                        }
                    </DialogContent>
                    <DialogActions>
                        <Controls.Button onClick={handleModalClose} variant={'text'} color='secondary'>
                            <Typography sx={{
                                ...selectedTheme?.typography?.Components?.button?.default,
                                ...selectedTheme?.typography?.Components?.button?.large,
                                textTransform: 'none'
                            }}>
                                Cancel
                            </Typography>
                        </Controls.Button>
                        <LoadingButton
                            form="email_form"
                            type="submit"
                            variant='contained'
                            color='primary'
                            loading={isLoading}
                            loadingPosition={'end'}
                            endIcon={<Box component={'span'} />}
                            sx={{
                                ...selectedTheme?.button.default,
                                ...selectedTheme?.button?.contained?.primary,
                                ...(isLoading && selectedTheme.button.contained?.inherit),
                                px: isLoading ? 3.75 : 2.75
                            }}
                        >
                            <Typography sx={{
                                ...selectedTheme?.typography?.Components?.button?.default,
                                ...selectedTheme?.typography?.Components?.button?.large,
                                textTransform: 'none'
                            }}>
                                Save
                            </Typography>
                        </LoadingButton>
                    </DialogActions>
                </Dialog>
            </>
        )
    } else {
        return (
            <>
                {
                    getEmailForm(modalEditData, submitForm, typeOptions, type, autoSubmit, selectedTheme)
                }
            </>
        )
    }
};
